import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NbIconConfig } from '@nebular/theme';
import { Subscription } from 'rxjs';
import { TutorService } from '../../../../services/tutor.service';
import { AI_SETTINGS_EMBEDDED_SECTION } from '../ai-settings/ai-settings.component';
import { filterSectionsByAvailableSettings } from '../ai-settings/ai-settings.sections';

export type TutorKnowledgeTabKey = 'base' | 'usage';

interface TutorKnowledgeTab {
  key: TutorKnowledgeTabKey;
  titleKey: string;
  hintKey: string;
  icon: string | NbIconConfig;
}

/** „Wissensbasis" = die verknüpften Ordner; „Quellen & Suche" = der
 *  KI-Konfigurationsabschnitt `knowledge` (Wissens-Modus, Quellenangaben,
 *  Web-Suche), vorher ein Unterpunkt von KI-Konfiguration. */
const TABS: TutorKnowledgeTab[] = [
  {
    key: 'base',
    titleKey: 'pages.tutors.knowledgeTabs.base',
    hintKey: 'pages.tutors.knowledgeTabs.baseHint',
    icon: { icon: 'knowledge', pack: 'al' },
  },
  {
    key: 'usage',
    titleKey: 'pages.tutors.knowledgeTabs.usage',
    hintKey: 'pages.tutors.knowledgeTabs.usageHint',
    icon: 'search-outline',
  },
];

/** Abschnitt der KI-Konfiguration hinter dem Reiter „Quellen & Suche". */
const USAGE_SECTION = 'knowledge';

/**
 * Agent › Wissen — Wissensbasis und „Quellen & Suche" als Reiter einer Seite
 * (/tutors/:id/knowledge/<key>), nach dem Muster von „Verhalten &
 * Fähigkeiten" (TutorSetupComponent).
 *
 * „Wissensbasis" braucht keine Einstellungen und steht sofort. „Quellen &
 * Suche" erscheint nur, wenn die Vorlage des Agenten diese Einstellungen hat —
 * das entscheidet eine eigene, frische Abfrage für genau diesen Agenten, nicht
 * der geteilte `tutorSettings$`: der hält beim Wechsel noch die Einstellungen
 * des vorher geöffneten Agenten (Twin ohne Abschnitt → Direktlink auf
 * „Quellen & Suche" wäre auf „Wissensbasis" umgeleitet worden). Ein Direktlink
 * auf „Quellen & Suche" wartet auf diese Antwort; scheitert sie, bleibt
 * „Wissensbasis".
 */
@Component({
  selector: 'app-tutor-knowledge',
  templateUrl: './tutor-knowledge.component.html',
  styleUrl: './tutor-knowledge.component.scss',
  providers: [{ provide: AI_SETTINGS_EMBEDDED_SECTION, useValue: USAGE_SECTION }],
})
export class TutorKnowledgeComponent implements OnInit, OnDestroy {
  tutorId = '';
  tabs: TutorKnowledgeTab[] = TABS.filter((t) => t.key === 'base');
  /** Steht fest, ob es „Quellen & Suche" für diesen Agenten gibt? */
  private usageKnown = false;
  active: TutorKnowledgeTabKey = 'base';
  private wanted: string | null = null;
  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tutorService: TutorService
  ) {}

  /** Tabset zeigen — außer ein Direktlink will „Quellen & Suche", bevor
   *  feststeht, ob es den Reiter gibt (sonst stünde kurz „Wissensbasis" da). */
  get ready(): boolean {
    return this.usageKnown || this.wanted !== 'usage';
  }

  ngOnInit(): void {
    this.tutorId = this.route.parent?.snapshot.paramMap.get('tutorId') ?? '';
    this.subs.push(
      this.route.paramMap.subscribe((params) => {
        this.wanted = params.get('tab');
        this.select();
      }),
      this.tutorService.fetchTutorSettings(this.tutorId).subscribe({
        next: (settings) => {
          const hasUsage = filterSectionsByAvailableSettings(settings || []).some(
            (s) => s.key === USAGE_SECTION
          );
          if (hasUsage) this.tabs = TABS;
          this.usageKnown = true;
          this.select();
        },
        error: () => {
          this.usageKnown = true;
          this.select();
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private select(): void {
    if (!this.ready) return;
    const hit = this.tabs.find((t) => t.key === this.wanted);
    if (hit) {
      this.active = hit.key;
    } else {
      this.router.navigate(['/tutors', this.tutorId, 'knowledge', 'base'], { replaceUrl: true });
    }
  }

  onTabChange(tab: { tabId: string }): void {
    if (tab?.tabId && tab.tabId !== this.active) {
      this.router.navigate(['/tutors', this.tutorId, 'knowledge', tab.tabId], { replaceUrl: true });
    }
  }

  trackTab = (_: number, t: TutorKnowledgeTab) => t.key;
}
